import { Container } from 'inversify'
import { ref, watch } from 'vue'

import {
  Event,
  HandballEventDetailId,
  IncidentId,
  Participant,
  TeamSide,
  VolleyballIncidentId,
} from '@collector/sportsapi-client-legacy'
import { ProbableIncident } from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'
import { ButtonColor } from '@mobile/components/Button/ButtonTypes'
import {
  getAwayTeam,
  getHomeTeam,
  getOppositeTeam,
  getTeamSide,
} from '@mobile/reusables/entityUtils'
import {
  getOppositeTeamSideFromEventDetail,
  getTeamSideFromEventDetail,
} from '@mobile/reusables/eventUtils'
import { RelationIoCBindings } from '@mobile/views/Relation/Shared/RelationDependencies/types'

import { ActiveParticipant } from './ActiveParticipant'
import { VolleyballRelationIoCBindings } from './VolleyballRelationDependencies'

const IncidentsChangingActiveParticipant: IncidentId[] = [
  VolleyballIncidentId.Serve,
]

const IncidentsRelatedToActiveParticipant: IncidentId[] = [
  ...IncidentsChangingActiveParticipant,
  VolleyballIncidentId.InPossession,
]

export function bindActiveParticipant(ioc: Container): void {
  let unwatch: () => void

  ioc
    .bind(VolleyballRelationIoCBindings.ActiveParticipant)
    .toDynamicValue(async (ctx) => {
      const [probableIncidents, probableEvent] = await Promise.all([
        ctx.container.getAsync(RelationIoCBindings.ProbableIncidents),
        ctx.container.getAsync(RelationIoCBindings.ProbableEvent),
      ])

      const [lastIncident] = probableIncidents.value

      return ref(getActiveParticipant(lastIncident, probableEvent.value))
    })
    .inSingletonScope()
    .onActivation(async (ctx, activeParticipant) => {
      const [probableIncidents, probableEvent] = await Promise.all([
        ctx.container.getAsync(RelationIoCBindings.ProbableIncidents),
        ctx.container.getAsync(RelationIoCBindings.ProbableEvent),
      ])

      unwatch = watch(probableIncidents, () => {
        const [lastIncident] = probableIncidents.value
        const isLastIncidentRelatedToActiveParticipant =
          IncidentsRelatedToActiveParticipant.find(
            (incidentId) => incidentId === lastIncident?.incident_id,
          )

        if (isLastIncidentRelatedToActiveParticipant) {
          activeParticipant.value = getActiveParticipant(
            lastIncident,
            probableEvent.value,
          )
        }
      })

      return activeParticipant
    })
    .when(() => true)
    .onDeactivation(async () => unwatch())
}

function getActiveParticipant(
  lastIncident: ProbableIncident | undefined,
  probableEvent: Event,
): ActiveParticipant {
  const shouldChangeActiveParticipantToOpposite =
    IncidentsChangingActiveParticipant.includes(
      Number(lastIncident?.incident_id),
    ) ?? false

  return lastIncident === undefined || lastIncident.participant_id === null
    ? getActiveParticipantFromEventDetail(
        probableEvent,
        HandballEventDetailId.StartWithPossessionTeam,
        shouldChangeActiveParticipantToOpposite,
      )
    : getActiveParticipantFromIncident(
        probableEvent,
        lastIncident,
        shouldChangeActiveParticipantToOpposite,
      )
}

function getActiveParticipantFromEventDetail(
  probableEvent: Event,
  eventDetailId: HandballEventDetailId,
  shouldChangeActiveParticipantToOpposite: boolean,
): ActiveParticipant {
  const side = shouldChangeActiveParticipantToOpposite
    ? getOppositeTeamSideFromEventDetail(probableEvent.details, eventDetailId)
    : getTeamSideFromEventDetail(probableEvent.details, eventDetailId)

  const participant =
    side === TeamSide.Home
      ? getHomeTeam(probableEvent.participants)
      : getAwayTeam(probableEvent.participants)

  return {
    participant: participant || null,
    side,
    color: side,
  }
}

function getActiveParticipantFromIncident(
  probableEvent: Event,
  lastActiveParticipantRelatedIncident: ProbableIncident,
  shouldChangeActiveParticipantToOpposite: boolean,
): { side: TeamSide; participant: Participant; color: ButtonColor } {
  const participant = probableEvent.participants.find(
    ({ id }) => id === lastActiveParticipantRelatedIncident.participant_id,
  )

  if (!participant) {
    throw Error(
      'Incidents related to active participant must be related to participant',
    )
  }

  const { participant_id } = lastActiveParticipantRelatedIncident
  if (
    shouldChangeActiveParticipantToOpposite &&
    participant_id !== participant.id
  ) {
    const oppositeParticipant = getOppositeTeam(
      probableEvent.participants,
      participant,
    )!
    const side = getTeamSide(probableEvent.participants, oppositeParticipant.id)

    return {
      participant: oppositeParticipant,
      color: side,
      side,
    }
  }

  const side = getTeamSide(probableEvent.participants, participant.id)

  return {
    participant,
    color: side,
    side,
  }
}
